import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  House,
  Users,
  Buildings,
  ClipboardText,
  SignOut,
  PlusCircle,
  ShieldCheck,
} from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import Logo from "@/components/Logo";

const navItems = [
  { to: "/admin-dashboard", end: true, label: "Dashboard", icon: House, testId: "nav-dashboard" },
  { to: "/admin-dashboard/landlords", label: "Landlords", icon: Users, testId: "nav-landlords" },
  { to: "/admin-dashboard/properties", label: "Properties", icon: Buildings, testId: "nav-properties" },
  { to: "/admin-dashboard/inspections", label: "Inspections", icon: ClipboardText, testId: "nav-inspections" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/admin-dashboard" className="flex items-center gap-3" data-testid="brand-link">
            <Logo size={38} />
            <div className="hidden md:block leading-tight border-l border-[#E5E7EB] pl-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#475569] font-semibold">
                Property Management
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <Link
                to="/admin-dashboard/console"
                className="hidden sm:inline-flex trent-btn-secondary"
                data-testid="header-admin-link"
              >
                <ShieldCheck weight="bold" size={16} /> Admin
              </Link>
            )}
            <button
              onClick={() => navigate("/admin-dashboard/inspections/new")}
              className="trent-btn-accent hidden sm:inline-flex"
              data-testid="new-inspection-btn"
            >
              <PlusCircle weight="bold" size={18} /> New Inspection
            </button>
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-[#1E3A5F]" data-testid="current-user-name">
                {user?.name || "User"}
              </span>
              <span className="text-[11px] text-[#6B7280]">{user?.email}</span>
            </div>
            <button
              onClick={logout}
              className="p-2 border border-[#E5E7EB] rounded-md hover:bg-[#F3F4F6]"
              data-testid="logout-btn"
              title="Sign out"
            >
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] flex">
        {/* Sidebar */}
        <aside className="hidden md:block w-56 border-r border-[#E5E7EB] min-h-[calc(100vh-64px)] bg-white">
          <nav className="p-3 space-y-1">
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#1E3A5F] text-white"
                      : "text-[#111827] hover:bg-[#F3F4F6]"
                  }`
                }
              >
                <n.icon size={18} weight="duotone" />
                {n.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink
                to="/admin-dashboard/console"
                data-testid="sidebar-admin-link"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium ${
                    isActive ? "bg-[#1E3A5F] text-white" : "text-[#1E3A5F] hover:bg-[#F3F4F6]"
                  }`
                }
              >
                <ShieldCheck size={18} weight="duotone" />
                Admin console
              </NavLink>
            )}
          </nav>
          <div className="px-4 py-6 mt-4 border-t border-[#E5E7EB] mx-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280] mb-2">
              Quick action
            </div>
            <button
              onClick={() => navigate("/admin-dashboard/inspections/new")}
              className="trent-btn-primary w-full"
              data-testid="sidebar-new-inspection"
            >
              <PlusCircle weight="bold" size={16} /> New Inspection
            </button>
          </div>
        </aside>

        <main className="flex-1 p-4 md:p-8 lg:p-10 min-w-0">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#E5E7EB] z-30 grid grid-cols-4">
        {navItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`mobile-${n.testId}`}
            className={({ isActive }) =>
              `flex flex-col items-center py-2 text-[10px] uppercase tracking-wider ${
                isActive ? "text-[#1E3A5F] font-bold" : "text-[#6B7280]"
              }`
            }
          >
            <n.icon size={20} weight="duotone" />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <Toaster richColors position="top-right" />
    </div>
  );
}
